import { PdfImportController } from '@/controllers/admin/pdfImport'
import {
  PdfImportSearchItem,
  PdfImportSearchParams,
  PdfImportSortField,
  PdfImportSortOrder,
  PdfImportSubmission
} from '@/interfaces/admin/pdfImport'
import { postError, postSuccess } from '@/helpers/notification'
import i18n from '@/locales'
import { Module } from 'vuex'

export interface PdfImportsState {
  items: PdfImportSearchItem[];
  loading: boolean;
  matchCount: number | null;
  filter: {
    search: string;
    status?: string;
    createdAfter?: string;
    createdBefore?: string;
    updatedAfter?: string;
    updatedBefore?: string;
  };
  pagination: {
    limit: number;
    offset: number;
    hasMore: boolean;
  };
  sort: {
    field: PdfImportSortField;
    direction: 'asc' | 'desc';
  };
  totalCount: number;
}

const initialState: PdfImportsState = {
  filter: { search: '' },
  items: [],
  loading: false,
  matchCount: null,
  pagination: {
    limit: 100,
    offset: 0,
    hasMore: false
  },
  sort: {
    field: 'updated',
    direction: 'desc'
  },
  totalCount: 0
};

const adminPdfImports: Module<PdfImportsState, any> = {
  namespaced: true,

  state: { ...initialState },

  getters: {
    visiblePdfImports: (state) => state.items,
    isLoading: state => state.loading,
    currentSort: state => state.sort,
    currentFilter: state => state.filter,
    canLoadMore: state => state.pagination.hasMore,
    totalCount: state => state.totalCount,
    matchCount: state => state.matchCount
  },

  mutations: {
    RECEIVE_PDF_IMPORTS(state, { items, totalCount, matchCount, reset = false }) {
      if (reset) {
        state.items = items;
      } else {
        state.items = [...state.items, ...items];
      }
      state.totalCount = totalCount;
      state.matchCount = matchCount;
    },

    UPDATE_ITEMS(state, { items, totalCount, matchCount }: { items: PdfImportSearchItem[], totalCount: number, matchCount: number | null }) {
      // Silently update existing items without triggering loading state
      // Preserves scroll position and provides smooth updates during polling
      const itemMap = new Map(items.map((item: PdfImportSearchItem) => [item.id, item]));
      const existingIds = new Set(state.items.map((item: PdfImportSearchItem) => item.id));

      // Find new items that don't exist in current state (e.g., newly created imports)
      const newItems = items.filter((item: PdfImportSearchItem) => !existingIds.has(item.id));

      // Update existing items
      const updatedItems = state.items.map((existingItem: PdfImportSearchItem) => {
        const updatedItem = itemMap.get(existingItem.id);

        if (updatedItem) {
          // Detect publish completion: locked changed from true to false AND was publishing
          // Don't show toast for PROCESSING → COMPLETED (Textract ingestion)
          if (existingItem.locked && !updatedItem.locked && existingItem.status === 'PUBLISHING') {
            // Fetch publish summary and show toast
            PdfImportController.getLatestPublishSummary(updatedItem.id)
              .then((progress) => {
                const importName = updatedItem.name;

                if (progress.success === progress.total && progress.failed === 0) {
                  // All pages published successfully
                  postSuccess({
                    title: i18n.global.t('success'),
                    text: i18n.global.t('pdfImport.publishingFinishedSuccess', {
                      importName,
                      count: progress.success
                    })
                  });
                } else if (progress.success === 0) {
                  // No pages published
                  postError({
                    title: i18n.global.t('error'),
                    text: i18n.global.t('pdfImport.publishingFinishedNone', {
                      importName
                    })
                  });
                } else {
                  // Some pages failed
                  postError({
                    title: i18n.global.t('error'),
                    text: i18n.global.t('pdfImport.publishingFinishedPartial', {
                      importName,
                      success: progress.success,
                      failed: progress.failed
                    })
                  });
                }
              })
              .catch((error) => {
                // Silently fail if we can't fetch summary (e.g., 404 if no publish jobs)
                console.error('Failed to fetch publish summary:', error);
              });
          }

          // Detect reset completion: RESETTING → previous completed status (COMPLETED or PUBLISHED)
          if (existingItem.status === 'RESETTING' && !updatedItem.locked) {
            postSuccess({
              title: i18n.global.t('success'),
              text: i18n.global.t('pdfImport.resetFinished', { importName: updatedItem.name })
            });
          }

          return updatedItem;
        }

        return existingItem;
      });

      // Prepend new items to the top (newly created imports appear first)
      state.items = [...newItems, ...updatedItems];

      state.totalCount = totalCount;
      state.matchCount = matchCount;
    },

    START_LOADING(state) {
      state.loading = true;
    },

    FINISH_LOADING(state) {
      state.loading = false;
    },

    UPDATE_FILTER(state, filter) {
      state.filter = { ...filter };
      state.pagination.offset = 0;
    },

    UPDATE_SORT(state, sort) {
      state.sort = { ...sort };
      state.pagination.offset = 0;
    },

    UPDATE_PAGINATION(state, { offset, limit, hasMore }) {
      state.pagination.offset = offset;
      state.pagination.limit = limit || state.pagination.limit;
      state.pagination.hasMore = hasMore;
    },

    RESET_STATE(state) {
      Object.assign(state, JSON.parse(JSON.stringify(initialState)));
    }
  },

  actions: {
    async initialize({ commit, dispatch }) {
      commit('RESET_STATE');
      await dispatch('loadPdfImports', { reset: true });
    },

    async loadMore({ state, dispatch }) {
      if (!state.pagination.hasMore || state.loading) return;

      const nextOffset = state.pagination.offset + state.pagination.limit;
      await dispatch('loadPdfImports', { offset: nextOffset });
    },

    async updateSearch({ state, commit, dispatch }, searchTerm) {
      commit('UPDATE_FILTER', { ...state.filter, search: searchTerm });
      await dispatch('loadPdfImports', { reset: true });
    },

    async updateFilters({ state, commit, dispatch }, filters) {
      const updatedFilter = { ...state.filter };

      if (filters.status) {
        updatedFilter.status = filters.status;
      } else {
        delete updatedFilter.status;
      }

      if (filters.created) {
        const [start, end] = filters.created.replace(/[\[\]]/g, '').split(' TO ');
        updatedFilter.createdAfter = start !== '*' ? start : undefined;
        updatedFilter.createdBefore = end !== 'NOW' ? end : undefined;
      } else {
        delete updatedFilter.createdAfter;
        delete updatedFilter.createdBefore;
      }

      if (filters.updated) {
        const [start, end] = filters.updated.replace(/[\[\]]/g, '').split(' TO ');
        updatedFilter.updatedAfter = start !== '*' ? start : undefined;
        updatedFilter.updatedBefore = end !== 'NOW' ? end : undefined;
      } else {
        delete updatedFilter.updatedAfter;
        delete updatedFilter.updatedBefore;
      }

      commit('UPDATE_FILTER', updatedFilter);
      await dispatch('loadPdfImports', { reset: true });
    },

    async clearFilters({ commit, dispatch }) {
      commit('UPDATE_FILTER', { search: '' });
      await dispatch('loadPdfImports', { reset: true });
    },

    async updateSort({ commit, dispatch }, sort) {
      commit('UPDATE_SORT', sort);
      await dispatch('loadPdfImports', { reset: true });
    },

    async loadPdfImports({ state, commit }, { offset, reset = false } = {}) {
      try {
        commit('START_LOADING');

        const currentOffset = offset !== undefined ? offset : (reset ? 0 : state.pagination.offset);

        const params: PdfImportSearchParams = {
          q: state.filter.search || undefined,
          limit: state.pagination.limit,
          offset: currentOffset,
          sort: state.sort.field,
          order: state.sort.direction.toUpperCase() as PdfImportSortOrder,
          status: state.filter.status as any,
          createdAfter: state.filter.createdAfter,
          createdBefore: state.filter.createdBefore,
          updatedAfter: state.filter.updatedAfter,
          updatedBefore: state.filter.updatedBefore
        };

        const response = await PdfImportController.searchPdfImports(params);
        const items = response.items || [];
        const totalCount = response.totalCount || 0;
        const matchCount = response.matchCount ?? null;

        commit('RECEIVE_PDF_IMPORTS', {
          items,
          totalCount,
          matchCount,
          reset
        });

        const hasMore = items.length > 0 && currentOffset + items.length < totalCount;
        commit('UPDATE_PAGINATION', {
          offset: currentOffset,
          limit: state.pagination.limit,
          hasMore
        });

        commit('FINISH_LOADING');
        return { items, totalCount };
      } catch (error) {
        commit('FINISH_LOADING');
        postError({
          title: i18n.global.t('error'),
          text: i18n.global.t('pdfImport.loadFailed')
        });
        throw error;
      }
    },

    async createImport({ dispatch }, submission: PdfImportSubmission) {
      try {
        return await PdfImportController.createPdfImport(submission);
      } catch (error) {
        throw error;
      }
    },

    async refreshItems({ state, commit }) {
      // Silent refresh for polling - updates data without loading state
      // Uses current pagination limit to maintain consistency and handle new imports
      try {
        // Calculate how many items to refresh based on what's currently loaded
        // This ensures we see new imports while maintaining scroll position
        const maxRefreshItems = 500;
        const currentlyLoaded = state.items.length;
        const paginationLimit = state.pagination.limit;

        // Use the greater of: items currently loaded OR default pagination limit
        // This handles both: (1) new imports appearing, (2) user has scrolled and loaded more
        const refreshLimit = Math.min(Math.max(currentlyLoaded, paginationLimit), maxRefreshItems);

        const params: PdfImportSearchParams = {
          q: state.filter.search || undefined,
          limit: refreshLimit,
          offset: 0,
          sort: state.sort.field,
          order: state.sort.direction.toUpperCase() as PdfImportSortOrder,
          status: state.filter.status as any,
          createdAfter: state.filter.createdAfter,
          createdBefore: state.filter.createdBefore,
          updatedAfter: state.filter.updatedAfter,
          updatedBefore: state.filter.updatedBefore
        };

        const response = await PdfImportController.searchPdfImports(params);
        const items = response.items || [];
        const totalCount = response.totalCount || 0;
        const matchCount = response.matchCount ?? null;

        commit('UPDATE_ITEMS', {
          items,
          totalCount,
          matchCount
        });

        return { items, totalCount };
      } catch (error) {
        // Silently fail during polling - don't disrupt user experience
        console.error('Failed to refresh PDF imports:', error);
      }
    }
  }
};

export default adminPdfImports;
